/**
 * Ημερομηνίες και ώρες «όπως στην Ελλάδα», όπου κι αν τρέχει ο κώδικας — ΕΝΑ σημείο για όλη την εφαρμογή.
 *
 * Ο server είναι UTC, η βάση και τα καταστήματα σε ώρα Ελλάδας. Δύο παγίδες που κόστισαν:
 *   - `new Date().toISOString().slice(0, 10)` = ημερομηνία UTC: από τις 00:00 ως τις 03:00 (02:00 τον
 *     χειμώνα) ώρα Ελλάδας είναι ακόμη «χθες» — Αρχική οργανισμού, Συμφωνία ημέρας, Νέα αγορά (15/10/2026).
 *     Για «σήμερα» → athensYmd().
 *   - `hour12: false` με el-GR δίνει «24:15» μετά τα μεσάνυχτα (κύκλος h24 σε Chrome/Node), ενώ χωρίς
 *     επιλογή δίνει «12:15 π.μ.». Για ώρα → fmtClock() / fmtDateClock() (`hourCycle: 'h23'` → «00:15»).
 *     Προσοχή: αν δοθούν ΚΑΙ τα δύο, το hour12 νικά το hourCycle — ποτέ hour12 μαζί του.
 *
 * Οι ημερομηνίες «YYYY-MM-DD» είναι ημερολογιακές (χωρίς ζώνη): η αριθμητική τους γίνεται στο UTC μεσημέρι,
 * ώστε η αλλαγή ώρας (π.χ. 25/10/2026 04:00 → 03:00) να μη μετακινεί τη μέρα.
 */

export const ATHENS_TZ = 'Europe/Athens';
const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

const YMD_FMT = new Intl.DateTimeFormat('en-GB', { timeZone: ATHENS_TZ, year: 'numeric', month: '2-digit', day: '2-digit' });

type Instant = Date | number | string | null | undefined;

/** Στιγμή → Date (άκυρο / κενό → null). */
const toDate = (v: Instant): Date | null => {
  if (v === null || v === undefined || v === '') return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const isYmd = (s: unknown): s is string => typeof s === 'string' && YMD_RE.test(s);

/** Η ημερομηνία Ελλάδας («YYYY-MM-DD») μιας στιγμής — χωρίς όρισμα, η ΣΗΜΕΡΙΝΗ. Άκυρο → ''. */
export function athensYmd(at: Date | number = Date.now()): string {
  const d = toDate(at);
  if (!d) return '';
  const parts = YMD_FMT.formatToParts(d);
  const get = (type: string) => parts.find(p => p.type === type)?.value || '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** Ημερολογιακή πρόσθεση ημερών σε «YYYY-MM-DD». Άκυρο → ως έχει. */
export function addDaysYmd(ymd: string, days: number): string {
  const m = YMD_RE.exec(ymd);
  if (!m) return ymd;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12));
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Η ημερομηνία Ελλάδας πριν από `days` ημέρες (0 = σήμερα, 6 = αρχή μιας εβδομάδας που τελειώνει σήμερα). */
export const athensDaysAgoYmd = (days: number, now: Date | number = Date.now()): string => addDaysYmd(athensYmd(now), -days);

/* ───────── ώρα, πάντα 24ωρη ───────── */

export interface ClockOptions {
  /** «00:15:30» αντί για «00:15». */
  seconds?: boolean;
  /** 'athens' (προεπιλογή) = ώρα Ελλάδας· 'local' = η ζώνη του browser (όπως οι παλιές οθόνες). */
  zone?: 'athens' | 'local';
  /** Προεπιλογή el-GR. */
  locale?: string;
}

const clockParts = (opts: ClockOptions): Intl.DateTimeFormatOptions => ({
  hour: '2-digit',
  minute: '2-digit',
  ...(opts.seconds ? { second: '2-digit' as const } : {}),
  hourCycle: 'h23',
  ...(opts.zone === 'local' ? {} : { timeZone: ATHENS_TZ }),
});

/** «00:15» — ποτέ «24:15» ή «12:15 π.μ.». Άκυρο / κενό → ''. */
export function fmtClock(value: Instant, opts: ClockOptions = {}): string {
  const d = toDate(value);
  return d ? d.toLocaleTimeString(opts.locale || 'el-GR', clockParts(opts)) : '';
}

/** «24/10, 00:15» — με `year` «24/10/2026, 00:15». Άκυρο / κενό → ''. */
export function fmtDateClock(value: Instant, opts: ClockOptions & { year?: boolean } = {}): string {
  const d = toDate(value);
  if (!d) return '';
  return d.toLocaleString(opts.locale || 'el-GR', {
    day: 'numeric',
    month: 'numeric',
    ...(opts.year ? { year: 'numeric' as const } : {}),
    ...clockParts(opts),
  });
}
